import { Badge, Box, Button, Flex, Heading, HStack, Switch, Text, Tooltip } from '@chakra-ui/react'
import { useWatch } from 'react-hook-form'
import { IconContext } from 'react-icons/'
import { AiOutlinePrinter } from 'react-icons/ai'
import { useDispatch, useSelector } from 'react-redux'

//API
import config, { APP_MODE } from '@/config'
import { useCustomer } from '@/features/customers/useCustomer'
import { disableAdminMode, enableAdminMode, selectOfferDetail } from '@/features/offers/slices/offerDetail.slice'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import { UserEditRight, UserRole } from '@/features/users/constants'
import { authSelector } from '@/stores/auth/authSlice'
import { showPrintOfferModal } from '@/stores/offers/offersSlice'
import { isValidNonEmptyString } from '@/utils/stringHelper'
import { OfferBookingStatus } from '@eapproval/offer-shared'
import { useMemo } from 'react'
import EditingUsersDisplay from './EditingUsersDisplay'

const OfferHeader = () => {
  //Redux
  const { role } = useSelector(selectOfferDetail)
  const { user } = useSelector(authSelector)

  const { useFetchAllCustomers } = useCustomer()

  const { useFetchAllSystemVariables } = useSystemVariable()

  const { data: fetchCustomersResponse } = useFetchAllCustomers({
    page: 1,
    pageSize: 0,
  })

  const isEQuotation = useWatch({ name: 'isEQuotation' })
  const offerId = useWatch({ name: '_id' })
  const offerName = useWatch({ name: 'projectName' })
  const quotationNumber = useWatch({ name: 'quotationNumber' })
  const offerStatus = useWatch({ name: 'status' })
  const customerId = useWatch({ name: 'customer' })
  const subSystems = useWatch({ name: 'subSystems' })
  const isClosed = useWatch({ name: 'isClosed' })
  const offerCloseMainReason = useWatch({ name: 'offerCloseMainReason' })
  const offerCloseDetailedReason = useWatch({ name: 'offerCloseDetailedReason' })
  const isActive = useWatch({ name: 'isActive' })
  const isSOP = useWatch({ name: 'isSOP' })

  const baseQuotationNumber = useWatch({ name: 'baseQuotationNumber' })
  const team = useWatch({ name: 'department' })

  const { data: systemVariablesTeamResponse, isLoading } = useFetchAllSystemVariables('team', { pageSize: 100 })

  const site = useWatch({ name: 'site' }) || ''
  const customerReference = useWatch({ name: 'customerReference' }) || ''
  //Translated
  const selectedCustomer = fetchCustomersResponse?.data.find((customer) => customer._id === customerId)
  const customerName = selectedCustomer
    ? `${isValidNonEmptyString(selectedCustomer.code) ? selectedCustomer.code : 'NO CUST CODE'}-${selectedCustomer.name}`
    : '(Empty Customer)'
  const subSystemNames = subSystems.map((subSystem) => subSystem?.projectName || '')
  const isShowBookingWarning = useMemo(() => {
    const isBooking = Object.values(OfferBookingStatus).includes(offerStatus)
    return (
      isBooking &&
      (selectedCustomer?.code == '' || selectedCustomer?.code == undefined || selectedCustomer?.isSAPActive == false)
    )
  }, [selectedCustomer, offerStatus])

  const isShowTempCustomerWarning = useMemo(() => {
    return selectedCustomer?.isTemporary
  }, [selectedCustomer])

  const showAdmin = useMemo(() => {
    return (
      user.role === UserRole.ADMIN ||
      user?.editRights.includes(UserEditRight.WORKFLOW) ||
      config.mode === APP_MODE.DEVELOPMENT
    )
  }, [user])

  const dispatch = useDispatch()

  const handleOpenPrintModal = () => {
    dispatch(
      showPrintOfferModal({
        offerId: offerId,
        subSystemNames: subSystemNames,
      })
    )
  }

  const goToSharepointLink = () => {
    const teamName = systemVariablesTeamResponse?.data.find((systemVariable) => systemVariable._id === team)?.name

    window.open(
      `https://siemensapc.sharepoint.com/teams/eApprvoal/Shared%20Documents/Forms/AllItems.aspx?id=%2Fteams%2FeApprvoal%2FShared%20Documents%2F${teamName}/${baseQuotationNumber}`
    )
  }

  return (
    <Flex bg="white" zIndex={4} max="100%" flexWrap="wrap" position={'sticky'} top="0" py="2" h="146px">
      <Flex w="100%" color={'red.500'}>
        Approval is based on Costsheet. Any information amended on the platform WILL NOT BE TRANSFERRED to costsheet or
        external proposal
      </Flex>

      <Flex w="100%">
        <Flex width="80%" direction="column">
          <Heading
            size="lg"
            noOfLines={2}
            whiteSpace={'wrap'}
            overflow="hidden"
            overflowY="auto"
            maxH="5rem"
            minH="3rem"
            w="100%"
            textOverflow="eclipse"
            color={'blue.400'}
            sx={{
              '::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            Offer: [{quotationNumber}]{offerName}
          </Heading>
          {/* Tag Stack */}
          <HStack>
            <Badge fontSize="md">{offerStatus}</Badge>
            {isClosed && (
              <Badge fontSize="md" colorScheme="pink">
                Closed ({offerCloseMainReason})
              </Badge>
            )}
            {!isActive && (
              <Badge fontSize="md" colorScheme="yellow">
                Superseded
              </Badge>
            )}
            {isShowTempCustomerWarning && (
              <Badge fontSize="md" colorScheme="pink">
                Temp Customer
              </Badge>
            )}
            {isShowBookingWarning && (
              <Badge fontSize="md" colorScheme="pink">
                Please Create Customer in SAP
              </Badge>
            )}
            {isSOP && (
              <Badge fontSize="md" colorScheme="green">
                Under the coverage of Security of Payment
              </Badge>
            )}
          </HStack>
          <Tooltip label={`${customerName} (Ref: ${customerReference}) (Site: ${site})`}>
            <Text as="b" fontSize="lg" noOfLines={1}>
              {customerName} (Ref: {customerReference}) (Site: {site})
            </Text>
          </Tooltip>
        </Flex>
        <Flex direction={'column'} w="20%" alignItems={'end'}>
          <Flex gap="2">
            <Text as="b">Role: </Text>
            <Text>{role}</Text>
          </Flex>

          <EditingUsersDisplay />
          <HStack>
            <Button onClick={handleOpenPrintModal}>
              <IconContext.Provider value={{ color: 'black', size: '20px' }}>
                <div>
                  <AiOutlinePrinter />
                </div>
              </IconContext.Provider>
              <Text ml="1">Print</Text>
            </Button>

            {!isEQuotation && process.env.NODE_ENV === 'production' && (
              <Button onClick={goToSharepointLink}>
                <IconContext.Provider value={{ color: 'black', size: '20px' }}>
                  <span>Go To Sharepoint</span>
                </IconContext.Provider>
              </Button>
            )}
       
          </HStack>
          {showAdmin && (
            <Flex alignItems="center" gap="2">
              <Box my="auto">Admin Edit Mode</Box>
              <Switch
                colorScheme="teal"
                size="md"
                onChange={(e) => {
                  if (e.target.checked) {
                    dispatch(enableAdminMode())
                  } else {
                    dispatch(disableAdminMode())
                  }
                }}
                mb="0"
              />
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default OfferHeader
